import { AlertTriangle, RefreshCw, X } from "lucide-react";
import { useEffect, useState } from "react";

export function formatRelative(ts: number, now: number = Date.now()) {
  if (!ts) return "—";
  const diff = Math.max(0, now - ts);
  const s = Math.floor(diff / 1000);
  if (s < 10) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  return new Date(ts).toLocaleString();
}

export interface LiveDataStatusProps {
  weatherError: Error | null;
  marineError: Error | null;
  tidesError: Error | null;
  /** Optional additional feed (e.g. tide heights curve). */
  tideHeightsError?: Error | null;
  isFetching: boolean;
  /** True only while a user-triggered retry is in flight. Keeps the button
   *  disabled for the full round-trip, independent of background polling. */
  isRetrying?: boolean;
  updatedAt: number;
  /** Whether *any* cached data is available. When false + errors present,
   *  the banner escalates to a hard "no data" message instead of stale-if-error. */
  hasCachedData?: boolean;
  onRetry: () => void | Promise<void>;
}

export function LiveDataStatus({
  weatherError,
  marineError,
  tidesError,
  tideHeightsError = null,
  isFetching,
  isRetrying = false,
  updatedAt,
  hasCachedData = true,
  onRetry,
}: LiveDataStatusProps) {
  const failed = [
    weatherError && "weather",
    marineError && "marine",
    tidesError && "tides",
    tideHeightsError && "tide chart",
  ].filter(Boolean) as string[];
  const hasError = failed.length > 0;
  const hardError = hasError && !hasCachedData;
  const busy = isRetrying || isFetching;
  const errorKey = failed.join("|") + (hardError ? "|hard" : "");

  // Dismissible non-blocking banner. Re-appears when a new error surfaces.
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  useEffect(() => {
    if (!hasError) setDismissedKey(null);
  }, [hasError]);
  const showBanner = hasError && dismissedKey !== errorKey;

  const bannerCls = hardError
    ? "rounded-xl border border-red-400/60 bg-red-500/15 px-4 py-3 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3 text-red-50"
    : "rounded-xl border border-amber-400/50 bg-amber-400/10 px-4 py-3 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3 text-amber-50";
  const iconCls = hardError ? "text-red-200 shrink-0 mt-0.5" : "text-amber-300 shrink-0 mt-0.5";
  const buttonCls = hardError
    ? "inline-flex items-center gap-2 rounded-md border border-red-300/40 bg-red-300/10 px-3 py-2 min-h-10 text-xs font-semibold hover:bg-red-300/20 disabled:opacity-60 disabled:cursor-not-allowed transition"
    : "inline-flex items-center gap-2 rounded-md border border-amber-300/40 bg-amber-300/10 px-3 py-2 min-h-10 text-xs font-semibold hover:bg-amber-300/20 disabled:opacity-60 disabled:cursor-not-allowed transition";
  const dismissCls = hardError
    ? "inline-flex items-center justify-center min-h-10 min-w-10 rounded-md text-red-100/80 hover:text-white hover:bg-white/10 transition"
    : "inline-flex items-center justify-center min-h-10 min-w-10 rounded-md text-amber-100/80 hover:text-white hover:bg-white/10 transition";

  return (
    <div className="space-y-2">
      {showBanner && (
        <div
          role={hardError ? "alert" : "status"}
          aria-live={hardError ? "assertive" : "polite"}
          data-testid="live-data-status-error"
          data-severity={hardError ? "hard" : "stale"}
          className={bannerCls}
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <AlertTriangle size={18} className={iconCls} aria-hidden />
            <div className="text-sm min-w-0">
              {hardError ? (
                <>
                  <p className="font-semibold">
                    Live {failed.join(" + ")} feed unavailable — no cached reading to fall back on.
                  </p>
                  <p className="opacity-80 mt-0.5 text-xs">
                    We can't reach the upstream source right now. Try again in a few minutes, or
                    check the individual widgets below for any partial data.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold">
                    Live {failed.join(" + ")} feed unavailable — showing last successful report.
                  </p>
                  <p className="opacity-80 mt-0.5 text-xs">
                    Last updated {formatRelative(updatedAt)}. Readings may be out of date;
                    we'll keep retrying in the background.
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              type="button"
              onClick={onRetry}
              disabled={busy}
              aria-busy={busy}
              className={buttonCls}
            >
              <RefreshCw size={12} className={busy ? "animate-spin" : ""} aria-hidden />
              {isRetrying ? "Retrying…" : isFetching ? "Refreshing…" : "Retry"}
            </button>
            <button
              type="button"
              onClick={() => setDismissedKey(errorKey)}
              aria-label="Dismiss banner"
              className={dismissCls}
            >
              <X size={14} aria-hidden />
            </button>
          </div>
        </div>
      )}
      <div
        data-testid="live-data-status-ok"
        className="rounded-xl border border-border bg-card/60 backdrop-blur px-4 py-2.5 shadow-sm flex items-center justify-between gap-3 text-xs"
      >
        <div className="flex items-center gap-2 text-muted-foreground">
          <span
            className={`inline-block h-2 w-2 rounded-full ${
              hasError
                ? "bg-amber-400"
                : busy
                ? "bg-[var(--color-ember)] animate-pulse"
                : "bg-emerald-500"
            }`}
            aria-hidden
          />
          <span>
            {hasError
              ? "Stale — last good read"
              : isRetrying
              ? "Retrying…"
              : isFetching
              ? "Refreshing live data…"
              : "Live"} · Updated {formatRelative(updatedAt)}
          </span>
        </div>
        <button
          type="button"
          onClick={onRetry}
          disabled={busy}
          aria-busy={busy}
          aria-disabled={busy}
          className="inline-flex items-center justify-center min-h-11 px-3 -mx-2 gap-1.5 rounded-md text-muted-foreground hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Refresh live data"
        >
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} aria-hidden />
          {isRetrying ? "Retrying" : "Refresh"}
        </button>
      </div>
    </div>
  );
}


